import gql from 'graphql-tag';

import { Query, Mutation } from 'core';
import { USER_ERROR_FIELDS } from 'core/errors';

const CARD_FIELDS = gql`
  fragment cardFields on CreditCard {
    id
    stripeId
    brand
    country
    name
    expMonth
    expYear
    lastDigits
    isDefault
  }
`;

const SEATS_USAGE = gql`
  fragment seatsUsage on SeatsUsage {
    id
    usedSpaceSeats
    usedProjectsSeats
    usedInvitesSeats
    unusedSeats
    includedPlanSeats
    purchasedSeats
    totalSubSeats
    seatsChange
  }
`;

const SUBSCRIPTION = gql`
  ${SEATS_USAGE}
  fragment subscription on BillingSubscription {
    id
    scaSecret
    currentPeriodEnd
    nextAmount
    cancelAtPeriodEnd

    plan {
      id
      name
    }
    seats {
      ...seatsUsage
    }
  }
`;

const SUBSCRIPTION_PREVIEW = gql`
  ${SEATS_USAGE}
  fragment subscriptionPreview on SubscriptionPreview {
    id
    totalAmount
    perSeatAmount
    initialAmount
    billingStarts
    prorationAmount
    prorationTimestamp
    discountAmount
    seats {
      ...seatsUsage
    }
  }
`;

const PROFILE_PREVIEW_SUBSCRIPTION = gql`
  ${CARD_FIELDS}
  ${SUBSCRIPTION}
  ${SUBSCRIPTION_PREVIEW}
  ${USER_ERROR_FIELDS}
  query ProfilePreviewSubscription(
    $spaceSlug: String!
    $coupon: String
    $extraSeats: Int
  ) {
    profile(username: $spaceSlug) {
      result(username: $spaceSlug) {
        id
        username
        countryCode
        taxId
        hasValidPaymentMethod
        inSpace {
          id
          trialPeriodEnd
        }
        billingSubscriptions {
          ...subscription
        }
        creditCards {
          ...cardFields
        }
        subscriptionPreview(coupon: $coupon, extraSeats: $extraSeats) {
          ...subscriptionPreview
        }
        __typename
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ProfilePreviewSubscriptionQuery extends Query {
  static conf = {
    GQL: PROFILE_PREVIEW_SUBSCRIPTION,
    varNames: ['spaceSlug', 'coupon', 'extraSeats'],
    resultPath: 'profile.result',
    errorPath: 'profile.userErrors',
  };
}

const INITIATIVE_PREVIEW_SUBSCRIPTION = gql`
  ${CARD_FIELDS}
  ${SUBSCRIPTION}
  ${SUBSCRIPTION_PREVIEW}
  ${USER_ERROR_FIELDS}
  query InitiativeSubscriptions(
    $spaceSlug: String
    $coupon: String
    $extraSeats: Int
  ) {
    initiative(slug: $spaceSlug) {
      result {
        id
        slug
        countryCode
        taxId
        hasValidPaymentMethod
        inSpace {
          id
          trialPeriodEnd
        }
        billingSubscriptions {
          ...subscription
        }
        creditCards {
          ...cardFields
        }
        subscriptionPreview(coupon: $coupon, extraSeats: $extraSeats) {
          ...subscriptionPreview
        }
        __typename
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class InitiativePreviewSubscriptionQuery extends Query {
  static conf = {
    GQL: INITIATIVE_PREVIEW_SUBSCRIPTION,
    varNames: ['spaceSlug', 'coupon', 'extraSeats'],
    resultPath: 'initiative.result',
    errorPath: 'initiative.userErrors',
  };
}

const CREATE_SUBSCRIPTION_MUTATION = gql`
  ${USER_ERROR_FIELDS}
  mutation NewSubscription($subscriptionInput: BillingSubscribeInput) {
    billingSubscribe(subscriptionData: $subscriptionInput) {
      subscription {
        id
        scaSecret
        nextAmount
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class CreateSubscriptionMutation extends Mutation {
  static conf = {
    GQL: CREATE_SUBSCRIPTION_MUTATION,
    inputName: 'subscriptionInput',
    resultPath: 'billingSubscribe.subscription',
    errorPath: 'billingSubscribe.userErrors',
  };
}

const CANCEL_SUBSCRIPTION_MUTATION = gql`
  ${USER_ERROR_FIELDS}
  mutation CancelSubscription(
    $cancelSubscriptionInput: BillingCancelSubscriptionInput
  ) {
    billingCancel(subscriptionData: $cancelSubscriptionInput) {
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class CancelSubscriptionMutation extends Mutation {
  static conf = {
    GQL: CANCEL_SUBSCRIPTION_MUTATION,
    inputName: 'cancelSubscriptionInput',
    // resultPath: 'billingSubscribe.subscription',
    errorPath: 'billingCancel.userErrors',
  };
}

const PROFILE_CREDIT_CARDS = gql`
  ${CARD_FIELDS}
  ${USER_ERROR_FIELDS}
  query ProfileCreditCards($spaceSlug: String!) {
    profile(username: $spaceSlug) {
      result(username: $spaceSlug) {
        id
        username
        hasValidPaymentMethod
        creditCards {
          ...cardFields
        }
        __typename
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ProfileCreditCardsQuery extends Query {
  static conf = {
    GQL: PROFILE_CREDIT_CARDS,
    varNames: ['spaceSlug'],
    resultPath: 'profile.result',
    errorPath: 'profile.userErrors',
  };
}

const INITIATIVE_CREDIT_CARDS = gql`
  ${CARD_FIELDS}
  ${USER_ERROR_FIELDS}
  query ProfileCreditCards($spaceSlug: String) {
    initiative(slug: $spaceSlug) {
      result {
        id
        slug
        hasValidPaymentMethod
        creditCards {
          ...cardFields
        }
        __typename
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class InitiativeCreditCardsQuery extends Query {
  static conf = {
    GQL: INITIATIVE_CREDIT_CARDS,
    varNames: ['spaceSlug'],
    resultPath: 'initiative.result',
    errorPath: 'initiative.userErrors',
  };
}

const PROFILE_INVOICES = gql`
  ${USER_ERROR_FIELDS}
  query ProfileInvoices($spaceSlug: String!) {
    profile(username: $spaceSlug) {
      result(username: $spaceSlug) {
        id
        username
        invoices {
          id
          status
          amount
          periodStart
          periodEnd
          number
          paymentMethod
          downloadUrl
        }
        __typename
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ProfileInvoicesQuery extends Query {
  static conf = {
    GQL: PROFILE_INVOICES,
    varNames: ['spaceSlug'],
    resultPath: 'profile.result',
    errorPath: 'profile.userErrors',
  };
}

const INITIATIVE_INVOICES = gql`
  ${USER_ERROR_FIELDS}
  query InitiativeSubscriptions($spaceSlug: String) {
    initiative(slug: $spaceSlug) {
      result {
        id
        slug
        invoices {
          id
          status
          number
          periodStart
          periodEnd
          paymentMethod
          amount
          downloadUrl
        }
        __typename
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class InitiativeInvoicesQuery extends Query {
  static conf = {
    GQL: INITIATIVE_INVOICES,
    varNames: ['spaceSlug'],
    resultPath: 'initiative.result',
    errorPath: 'initiative.userErrors',
  };
}

export const ADD_PAYMENT_METHOD_MUTATION = gql`
  ${CARD_FIELDS}
  ${USER_ERROR_FIELDS}
  mutation AddPaymentMethod($paymentMethodData: AddPaymentMethodInput) {
    addPaymentMethod(paymentMethodData: $paymentMethodData) {
      paymentMethod {
        ...cardFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class AddPaymentMethodMutation extends Mutation {
  static conf = {
    GQL: ADD_PAYMENT_METHOD_MUTATION,
    inputName: 'paymentMethodData',
    resultPath: 'addPaymentMethod.paymentMethod',
    errorPath: 'addPaymentMethod.userErrors',
  };
}

export const DEFAULT_PAYMENT_METHOD_MUTATION = gql`
  ${USER_ERROR_FIELDS}
  mutation MakeDefaulPaymentMethodMutation(
    $paymentMethodData: MakeDefaultPaymentMethodInput
  ) {
    makeDefaultPaymentMethod(paymentMethodData: $paymentMethodData) {
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class MakeDefaulPaymentMethodMutation extends Mutation {
  static conf = {
    GQL: DEFAULT_PAYMENT_METHOD_MUTATION,
    inputName: 'paymentMethodData',
    errorPath: 'makeDefaultPaymentMethod.userErrors',
  };
}

export const DELETE_PAYMENT_METHOD_MUTATION = gql`
  ${USER_ERROR_FIELDS}
  mutation DeletePaymentMutation($paymentMethodData: DeletePaymentMethodInput) {
    deletePaymentMethod(paymentMethodData: $paymentMethodData) {
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class DeletePaymentMethodMutaton extends Mutation {
  static conf = {
    GQL: DELETE_PAYMENT_METHOD_MUTATION,
    inputName: 'paymentMethodData',
    errorPath: 'deletePaymentMethod.userErrors',
  };
}

export const ADD_SUBSCRIPTION_SEATS_MUTATION = gql`
  ${SUBSCRIPTION}
  ${USER_ERROR_FIELDS}
  mutation AddSubscriptionSeatsMutation($arguments: BillingAddSeatsInput) {
    billingAddSeats(arguments: $arguments) {
      subscription {
        ...subscription
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class AddSubscriptionSeatsMutation extends Mutation {
  static conf = {
    GQL: ADD_SUBSCRIPTION_SEATS_MUTATION,
    inputName: 'arguments',
    errorPath: 'billingAddSeats.userErrors',
  };
}

export const REMOVE_SUBSCRIPTION_SEATS_MUTATION = gql`
  ${SUBSCRIPTION}
  ${USER_ERROR_FIELDS}
  mutation RemoveSubscriptionSeatsMutation(
    $arguments: BillingRemoveSeatsInput
  ) {
    billingRemoveSeats(arguments: $arguments) {
      subscription {
        ...subscription
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class RemoveSubscriptionSeatsMutation extends Mutation {
  static conf = {
    GQL: REMOVE_SUBSCRIPTION_SEATS_MUTATION,
    inputName: 'arguments',
    errorPath: 'billingRemoveSeats.userErrors',
  };
}

const PROFILE_ALTER_SEATS_PREVIEW = gql`
  ${SUBSCRIPTION}
  ${SUBSCRIPTION_PREVIEW}
  ${USER_ERROR_FIELDS}
  query ProfileSeatsPreview(
    $spaceSlug: String!
    $addedSeats: Int!
    $removedSeats: Int!
  ) {
    profile(username: $spaceSlug) {
      result(username: $spaceSlug) {
        id
        username
        billingSubscriptions {
          id
          ...subscription
          addSeatsPreview(nSeats: $addedSeats) {
            ...subscriptionPreview
          }
          removeSeatsPreview(nSeats: $removedSeats) {
            ...subscriptionPreview
          }
        }
        __typename
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ProfileAlterSeatsPreviewQuery extends Query {
  static conf = {
    GQL: PROFILE_ALTER_SEATS_PREVIEW,
    varNames: ['spaceSlug', 'addedSeats', 'removedSeats'],
    resultPath: 'profile.result',
    errorPath: 'profile.userErrors',
  };
}

const INITIATIVE_ALTER_SEATS_PREVIEW = gql`
  ${SUBSCRIPTION}
  ${SUBSCRIPTION_PREVIEW}
  ${USER_ERROR_FIELDS}
  query InitiativeSeatsPreview(
    $spaceSlug: String
    $addedSeats: Int!
    $removedSeats: Int!
  ) {
    initiative(slug: $spaceSlug) {
      result {
        id
        slug
        billingSubscriptions {
          id
          ...subscription
          addSeatsPreview(nSeats: $addedSeats) {
            ...subscriptionPreview
          }
          removeSeatsPreview(nSeats: $removedSeats) {
            ...subscriptionPreview
          }
        }
        __typename
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class OrganizationAlterSeatsPreviewQuery extends Query {
  static conf = {
    GQL: INITIATIVE_ALTER_SEATS_PREVIEW,
    varNames: ['spaceSlug', 'addedSeats', 'removedSeats'],
    resultPath: 'initiative.result',
    errorPath: 'initiative.userErrors',
  };
}
